import type { ProductListingPage } from "@decocms/apps-commerce/types";
import { mapProductToAnalyticsItem } from "@decocms/apps-commerce/utils/productToAnalyticsItem";
import { HTMLWidget } from "~/types/widgets";
import SearchControls from "~/components/search/SearchControls";
import LoadMoreProductGrid from "~/components/search/LoadMoreProductGrid";
import ProductCard from "~/components/product/ProductCard";
import { useOffer } from "~/sdk/useOffer";
import { clx } from "@decocms/blocks/sdk/clx";
import { matchPathname } from "~/sdk/matchPathname";
import { withDynamicOutletHighlights, mergeCarouselSizeFilter } from "~/sdk/carouselSizeHref";
import { useEffect, useId } from "react";
import { sendEvent } from "~/sdk/analytics";
import type { SectionProps } from "~/types/deco";
import Breadcrumb from "~/components/ui/Breadcrumb";
import {
  CarouselSize,
  type CarouselSizeContent,
} from "~/components/ui/CarouselSize";
import Icon from "~/components/ui/Icon";
import type { ImageWidget } from "~/types/widgets";
import Image from "~/components/ui/Image";
import { sanitizeStoreUrl } from "~/utils/constants";
import type { Product } from "@decocms/apps-commerce/types";
import { Layout as CardLayout } from "~/components/product/ProductCard";
import CrossSellingShelf from "~/components/product/CrossSellingShelf";
import { inlineScript, useScript } from "@decocms/blocks/sdk/useScript";

// Definir tipo Columns localmente
type Columns = {
  mobile?: 1 | 2;
  desktop?: 2 | 3 | 4 | 5;
};
export interface Layout {
  basics?: {
    contentAlignment?: "Left" | "Center";
    oldPriceSize?: "Small" | "Normal";
    ctaText?: string;
    nameFormat?: "concat" | "productGroup" | "product";
  };
  elementsPositions?: {
    skuSelector?: "Top" | "Bottom";
    favoriteIcon?: "Top right" | "Top left";
  };
  productFlags?: {
    /** @description Show product flags **/
    showFlags?: boolean;
    flags?: Flag[];
  };
  hide?: {
    productName?: boolean;
    productDescription?: boolean;
    allPrices?: boolean;
    installments?: boolean;
    skuSelector?: boolean;
    cta?: boolean;
  };
  onMouseOver?: {
    image?: "Change image" | "Zoom image";
    card?: "None" | "Move up";
    showFavoriteIcon?: boolean;
    showSkuSelector?: boolean;
    showCardShadow?: boolean;
    showCta?: boolean;
  };
  /**
   * @title Pagination
   * @description Format of the pagination
   */
  pagination?: "show-more" | "pagination";
  /**
   * @title Use legacy VTEX loader
   * @description Marque como true se o campo "page" deste bloco usa o loader legado (productListingPage.ts, API Legacy Catalog da VTEX) em vez do moderno (productListingPageLean.ts, Intelligent Search). Só importa quando "pagination" for "show-more" — decide qual loader o botão "Carregar Mais" invoca sem reload. Deixe em branco/false para o loader moderno (padrão). Esse modo assume que o campo "page" deste bloco resolve pra um dos dois loaders de listagem VTEX já usados no site (moderno ou legado) — não se aplica a outros tipos de dado.
   */
  useLegacyLoader?: boolean;
}
/**
 * @titleBy title
 */
interface Flag {
  /** @title Title of the cluster to be detected */
  title: string;
  image: ImageWidget;
  mobile?: {
    width?: number;
    height?: number;
  };
  desktop?: {
    width?: number;
    height?: number;
  };
}

interface CrossSellingShelfProps {
  sectionTitle: string;
  products: Product[] | null;
  productsPerScrollDesktop: 1 | 2 | 3 | 4 | 5 | 6;
  cardLayout?: CardLayout;
  /** @description Used for analytics event, will not appear on interface */
  title?: string;
}
interface NotFoundProps {
  collections?: Collection[];
  crossSellingShelfProps?: CrossSellingShelfProps;
}
/**
 * @titleBy title
 */
interface Collection {
  title: string;
  description: string;
  url: string;
  image: ImageWidget;
}
export const useUrlRebased = (overrides: string | undefined, base: string) => {
  let url: string | undefined = undefined;
  if (overrides) {
    const temp = new URL(overrides, base);
    const final = new URL(base);
    final.pathname = temp.pathname;
    temp.searchParams.forEach((value, key) => {
      final.searchParams.set(key, value);
    });
    url = final.href;
  }
  return url;
};

export type PageResultProps = Pick<
  Props,
  "layout" | "cardLayout" | "startingPage" | "partial"
> & {
  layout?: Layout;
  cardLayout?: Layout;
  startingPage?: 0 | 1;
  url: string;
  partial?: "hideMore" | "hideLess";
  page: ProductListingPage;
};

function PageResult(props: PageResultProps) {
  const { layout, cardLayout, startingPage = 0, url, partial } = props;
  const page = props.page!;
  const { products, pageInfo } = page;

  // Verificação de segurança para garantir que os dados necessários estejam disponíveis
  if (!products || !pageInfo) {
    return (
      <div className="w-full flex justify-center items-center py-10">
        <span className="text-lg text-gray-500">Carregando produtos...</span>
      </div>
    );
  }

  const zeroIndexedOffsetPage = pageInfo?.currentPage - startingPage;
  const nextPageUrl = useUrlRebased(pageInfo.nextPage, url);
  const prevPageUrl = useUrlRebased(pageInfo.previousPage, url);
  const infinite = layout?.pagination === "show-more";
  const useLegacyLoader = layout?.useLegacyLoader ?? false;

  return (
    <div className="grid grid-flow-row grid-cols-1 place-items-center">
      <div
        className={clx(
          "pb-2 sm:pb-10",
          (!prevPageUrl || partial === "hideLess") && "hidden",
        )}
      >
        <a
          rel="prev"
          href={prevPageUrl}
          className="btn border border-[#ccc184] rounded-none hidden"
        >
          <span className="inline uppercase text-xs text-[#333333] tracking-wider">
            Mostrar menos produtos
          </span>
        </a>
      </div>

      {infinite
        ? (
          <LoadMoreProductGrid
            initialProducts={products}
            initialPageInfo={pageInfo}
            url={url}
            cardLayout={cardLayout}
            useLegacyLoader={useLegacyLoader}
          />
        )
        : (
          <>
            <div
              data-product-list
              className={clx(
                "grid items-center",
                "grid-cols-2 gap-x-1.5 gap-y-4.5",
                "sm:grid-cols-4",
                "w-full",
                "px-2.5", "lg:px-[50px]"
              )}
            >
              {products?.map((product, index) => (
                <ProductCard
                  key={`product-card-${product.productID}`}
                  product={product}
                  preload={index === 0}
                  layout={cardLayout}
                />
              ))}
            </div>

            <div className={clx("pt-2 sm:pt-10 w-full", "")}>
              <div className={clx("join")}>
                <a
                  rel="prev"
                  aria-label="previous page link"
                  href={prevPageUrl ?? "#"}
                  aria-disabled={!prevPageUrl}
                  className="btn btn-ghost join-item"
                >
                  <Icon id="ChevronRight" className="rotate-180" />
                </a>
                <span className="btn btn-ghost join-item">
                  Page {zeroIndexedOffsetPage + 1}
                </span>
                <a
                  rel="next"
                  aria-label="next page link"
                  href={nextPageUrl ?? "#"}
                  aria-disabled={!nextPageUrl}
                  className="btn btn-ghost join-item"
                >
                  <Icon id="ChevronRight" />
                </a>
              </div>
            </div>
          </>
        )}
    </div>
  );
}

const setPageQuerystring = (page: string, id: string) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  const element = document
    .getElementById(id)
    ?.querySelector("[data-product-list]");
  if (!element) {
    return;
  }
  // IntersectionObserver sempre chama o callback uma vez, de imediato, ao
  // chamar .observe() — reportando o estado ATUAL do elemento, não uma
  // transição real de scroll. Como [data-product-list] (a vitrine inteira)
  // já está visível no carregamento da página (fica logo abaixo do
  // header), esse primeiro disparo tratava o load da página como se fosse
  // "usuário rolou até aqui", já mutando a URL via history.replaceState
  // antes de qualquer scroll de verdade acontecer — o que, combinado com a
  // sincronização de URL do TanStack Router, disparava um ciclo de
  // remount que fazia o link de "Carregar mais" avançar sozinho (de
  // page=2 pra page=3) nos primeiros segundos após o load. Ignora esse
  // primeiro disparo — só reage a transições reais de interseção depois
  // dele.
  let isFirstCallback = true;
  new IntersectionObserver((entries) => {
    if (isFirstCallback) {
      isFirstCallback = false;
      return;
    }
    const url = new URL(location.href);
    const prevPage = url.searchParams.get("page");
    for (let it = 0; it < entries?.length; it++) {
      if (entries[it].isIntersecting) {
        url.searchParams.set("page", page);
      } else if (
        typeof history.state?.prevPage === "string" &&
        history.state?.prevPage !== page
      ) {
        url.searchParams.set("page", history.state.prevPage);
      }
    }
    history.replaceState({ prevPage }, "", url.href);
  }).observe(element);
};

const PLP_SCROLL_STORAGE_PREFIX = "dt-plp-scroll:";

// "Carregar mais" (`rel="next"`) é um link de verdade pra `?page=N` — sem
// isso, o clique dispara uma navegação normal do browser, que reseta o
// scroll pro topo da página. Do ponto de vista de quem clica, a lista de
// produtos "some" (a página inteira recarrega do zero), dando a impressão
// de que o botão não fez nada. Guarda a posição de scroll no sessionStorage
// antes de navegar e restaura assim que a próxima página carrega, na mesma
// posição aproximada onde o link estava.
function preserveScrollOnLoadMoreScript(containerId: string): string {
  return `(function() {
    var KEY = ${JSON.stringify(PLP_SCROLL_STORAGE_PREFIX)} + location.pathname;
    var stored = sessionStorage.getItem(KEY);
    if (stored) {
      sessionStorage.removeItem(KEY);
      var y = parseInt(stored, 10);
      if (!isNaN(y)) {
        requestAnimationFrame(function() {
          window.scrollTo(0, y);
        });
      }
    }
    var container = document.getElementById(${JSON.stringify(containerId)});
    var link = container && container.querySelector('a[rel="next"]');
    if (link) {
      link.addEventListener("click", function() {
        sessionStorage.setItem(KEY, String(window.scrollY));
      });
    }
  })();`;
}
function NotFound({ collections }: NotFoundProps) {
  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="mt-[30px] mb-25 flex flex-col items-center">
          <div className="font-['Poppins','Times_New_Roman',Times,serif] text-2xl uppercase mb-2 text-[#333333]">
            Resultado não encontrado
          </div>
          <div className="font-['Poppins',Helvetica,Arial,sans-serif] text-sm">
            Infelizmente não encontramos nenhum resultado
          </div>
        </div>
        {collections && (
          <div className="flex flex-col w-full mb-16 md:flex-row">
            {collections.map((collection) => {
              return (
                <div
                  className="px-2.5 w-full md:w-[350px] lg:w-[450px] 2xl:w-[650px]"
                  key={collection.title}
                >
                  <a href={sanitizeStoreUrl(collection.url)}>
                    <Image
                      width={650}
                      height={570}
                      alt={`Banner ${collection.title}`}
                      src={collection.image}
                      className="mb-[30px]"
                      loading="lazy"
                      fetchPriority="low"
                      decoding="async"
                    />
                  </a>
                  <div className="flex flex-col w-full items-center justify-center md:flex-row md:justify-end md:items-start">
                    <h2 className="text-lg mx-2.5 mb-2.5 text-right uppercase font-['Poppins','Times_New_Roman',Times,serif] md:mb-0 xl:text-2xl">
                      {collection.title}
                    </h2>
                    <div className="flex flex-col items-center w-full text-center md:text-left md:mx-2.5 md:w-[390px] md:items-start">
                      <span className="text-base font-['Poppins',Helvetica,Arial,sans-serif] leading-[22px] font-light whitespace-pre-line break-words">
                        {collection.description}
                      </span>
                      <a
                        className="font-['Poppins','Times_New_Roman',Times,serif] mb-10 w-50 h-10 flex items-center justify-center leading-[1.42857] text-sm p-0 tracking-[1.5px] rounded-none mt-[18px] bg-transparent border-[#ccc184] border-[1px] uppercase text-black cursor-pointer text-center md:mb-0"
                        href={sanitizeStoreUrl(collection.url)}
                      >
                        Ver Coleção
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
function TitleAndResult(
  {
    page,
    layout,
    cardLayout,
    customTitle,
    carouselSize,
    url,
    partial,
    startingPage = 0,
  }:
    & Omit<SectionProps<typeof loader>, "page">
    & {
      page: ProductListingPage;
      url: string;
      partial?: "hideMore" | "hideLess";
      startingPage?: 0 | 1;
    },
) {
  const container = useId();
  const { filters, breadcrumb, pageInfo, sortOptions } = page;
  const infinite = layout?.pagination === "show-more";
  const customTitleHTML = customTitle?.customTitle;
  let defaultTitle = "";
  if (breadcrumb?.itemListElement?.length) {
    const breadCrumbElements = breadcrumb.itemListElement;
    for (let i = 0; i < breadCrumbElements.length; i++) {
      let categoryName;
      if (i === 0) {
        categoryName = `<h1>${breadCrumbElements[i].name}`;
      } else {
        categoryName = breadCrumbElements[i].name;
      }
      defaultTitle += categoryName ?? "";
      if (i < breadCrumbElements.length - 1) {
        (defaultTitle as string) += " - ";
      } else {
        (defaultTitle as string) += "</h1>";
      }
    }
  }

  // `view_item_list` via sendEvent (mesmo caminho do `view_item`). O antigo
  // useSendEvent({ on: "view" }) só gravava `data-event` no HTML e dependia do
  // ANALYTICS_SCRIPT do @decocms/start, que este site não carrega — o evento
  // nunca chegava ao dataLayer (GA4 view_item_list e Pinterest viewcategory
  // mudos). O efeito roda no carregamento e de novo a cada navegação
  // client-side que troca a lista de produtos.
  const viewItemListKey = page.products?.map((p) => p.productID).join(",") ??
    "";
  useEffect(() => {
    if (!page.products?.length) return;
    sendEvent({
      name: "view_item_list",
      params: {
        item_list_name: breadcrumb?.itemListElement?.at(-1)?.name,
        item_list_id: breadcrumb?.itemListElement?.at(-1)?.item,
        items: page.products.map((product, index) =>
          mapProductToAnalyticsItem({
            ...useOffer(product.offers),
            index,
            product,
            breadcrumbList: page.breadcrumb,
          })
        ),
      },
    });
  }, [viewItemListKey]);

  return (
    <>
      <div
        id={container}
        className="w-full max-w-screen-2xl mx-auto"
      >
        {partial
          ? (
            <PageResult
              page={page}
              layout={layout}
              cardLayout={cardLayout}
              url={url}
              partial={partial}
              startingPage={startingPage}
            />
          )
          : (
            <>
              <div className="m-0">
                <div className="mb-2.5">
                  <SearchControls
                    sortOptions={sortOptions}
                    filters={filters}
                    productCount={pageInfo?.records ?? 0}
                  />
                </div>

                {carouselSize && <CarouselSize {...carouselSize} />}

                <div className="lg:px-[50px] mt-5 mb-2.5 h-5 flex lg:items-center pl-2.5 lg:pl-[50px]">
                  <Breadcrumb
                    itemListElement={breadcrumb?.itemListElement}
                    page="search"
                  />
                </div>

                {customTitleHTML || defaultTitle.length
                  ? (
                    <div className="w-full mt-2.5 mb-[50px] lg:px-[50px]">
                      <div
                        className="font-['Poppins'] not-italic font-normal text-start tracking-[0.2em] text-[#000000] m-0 capitalize mx-2.5 lg:mx-0 text-base"
                        dangerouslySetInnerHTML={{
                          __html: customTitleHTML ?? defaultTitle,
                        }}
                      />
                    </div>
                  )
                  : null}
              </div>
              <PageResult
                page={page}
                layout={layout}
                cardLayout={cardLayout}
                url={url}
                partial={partial}
                startingPage={startingPage}
              />
            </>
          )}
      </div>

      {!infinite && (
        <script
          type="module"
          dangerouslySetInnerHTML={{
            __html: useScript(
              setPageQuerystring,
              `${pageInfo?.currentPage}`,
              container,
            ),
          }}
        />
      )}
      {!infinite && (
        <script
          type="module"
          {...inlineScript(preserveScrollOnLoadMoreScript(container))}
        />
      )}
    </>
  );
}
function NotFound404({ collections, crossSellingShelfProps }: NotFoundProps) {
  return (
    <div>
      <div className="flex flex-col items-center max-w-screen overflow-hidden w-full px-4">
        <div className="mt-[30px] mb-8 lg:mb-18 flex flex-col items-center">
          <div className="font-['Poppins','Times_New_Roman',Times,serif] font-medium text-xl lg:text-2xl uppercase mb-2 text-[#333333] text-center">
            ESSA PÁGINA NÃO EXISTE
          </div>
          <div className="font-['Poppins',Helvetica,Arial,sans-serif] text-base font-semibold mb-2 text-[#333333] text-center">
            Possíveis causas:
          </div>
          <ul className="flex flex-wrap justify-center gap-x-6 text-xs lg:text-sm text-[#333333] font-normal list-disc pl-0 lg:w-[62%]">
            <li className="list-item">A página foi removida ou deletada;</li>
            <li className="list-item">
              O link utilizado está desatualizado ou quebrado;
            </li>
            <li className="list-item">
              O endereço (URL) foi digitado incorretamente.
            </li>
          </ul>
        </div>

        {collections && (
          <div className="flex flex-col w-full mb-0 md:flex-row md:flex-wrap md:justify-center gap-y-8">
            {collections.map((collection) => {
              return (
                <div
                  className="lg:px-2.5 w-full max-w-full md:w-[350px] lg:w-[450px] 2xl:w-[650px]"
                  key={collection.title}
                >
                  <a href={sanitizeStoreUrl(collection.url)}>
                    <Image
                      width={650}
                      height={570}
                      alt={`Banner ${collection.title}`}
                      src={collection.image}
                      className="mb-[30px] w-full h-auto object-cover"
                      loading="lazy"
                      fetchPriority="low"
                      decoding="async"
                    />
                  </a>
                  <div className="flex flex-col w-full items-center justify-center md:flex-row md:justify-end md:items-start">
                    <h2 className="text-lg mx-2.5 mb-2.5 text-right uppercase font-['Poppins','Times_New_Roman',Times,serif] md:mb-0 xl:text-2xl">
                      {collection.title}
                    </h2>
                    <div className="flex flex-col items-center w-full text-center md:text-left md:mx-2.5 md:w-[390px] md:items-start">
                      <span className="text-base font-['Poppins',Helvetica,Arial,sans-serif] leading-[22px] font-light break-words break-all">
                        {collection.description}
                      </span>
                      <a
                        className="font-['Poppins','Times_New_Roman',Times,serif] w-full max-w-50 h-10 flex items-center justify-center leading-[1.42857] text-sm tracking-[1.5px] rounded-none mt-[18px] bg-transparent border-[#ccc184] border-[1px] uppercase text-black cursor-pointer text-center mb-2 md:mb-0"
                        href={sanitizeStoreUrl(collection.url)}
                      >
                        Ver Coleção
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {crossSellingShelfProps && (
        <CrossSellingShelf
          sectionTitle={crossSellingShelfProps.sectionTitle}
          products={crossSellingShelfProps.products}
          productsPerScrollDesktop={crossSellingShelfProps
            .productsPerScrollDesktop}
          cardLayout={crossSellingShelfProps.cardLayout}
          title={crossSellingShelfProps.title}
        />
      )}
    </div>
  );
}

function SearchTitleAndResult(
  {
    page,
    collections,
    crossSellingShelfProps,
    url,
    partial,
    startingPage = 0,
    ...props
  }:
    & SectionProps<
      typeof loader
    >
    & {
      url: string;
      partial?: "hideMore" | "hideLess";
      startingPage?: 0 | 1;
    },
) {
  // Se não existe page, mostra not found 404
  if (!page) {
    return (
      <NotFound404
        collections={collections}
        crossSellingShelfProps={crossSellingShelfProps}
      />
    );
  }
  // Se existe page, mas não tem produtos
  if (!page.products || page.products.length === 0) {
    // Se for busca (tem ?q= na URL), mostra not found de busca. A VTEX
    // Intelligent Search não retorna pageInfo.pageTypes pra resultado de
    // busca por texto (só pra navegação por categoria) — checar o pageType
    // aqui nunca batia, então uma busca sem resultado sempre cai no 404
    // genérico ("ESSA PÁGINA NÃO EXISTE") em vez do aviso amigável de
    // busca. Detecta busca pela própria URL da requisição em vez de
    // depender de um campo que a API nunca preenche nesse caso.
    const isSearch = new URL(url).searchParams.has("q");
    if (isSearch) {
      return <NotFound collections={collections} />;
    }
    // Se não for busca, mostra not found 404
    return (
      <NotFound404
        collections={collections}
        crossSellingShelfProps={crossSellingShelfProps}
      />
    );
  }
  return (
    <TitleAndResult
      {...props}
      page={page as ProductListingPage}
      collections={collections}
      url={url}
      partial={partial}
      startingPage={startingPage}
    />
  );
}
// Interface para prop extra

/**
 * @titleBy title
 */
export interface CustomTitle {
  /** @description Department/category title to be shown on CMS list. Does not impact storefront. */
  title: string;
  /** @description Custom title for a specific department or category. */
  customTitle: HTMLWidget;
  /** @description RegExp to enable this banner on specific URLs. Use /feminino/* to display the banner on every subcategory of feminino (but not on feminino itself)  */
  matcher: string;
}
export interface LayoutColumns {
  /**
   * @description Number of products per line on grid
   */
  columns?: Columns;
}
export interface Props {
  /** @title Integration Configuration */
  page: ProductListingPage | null;
  layout?: LayoutColumns & Layout;
  cardLayout?: Layout;
  /** @description Collections to appear at the "Not found" page. */
  collections?: Collection[];
  /** @description Put the more specific titles above the less specific ones. That way, on pages where they might conflict, the more specific one will be prioritize. */
  customTitles?: CustomTitle[];
  carouselSize?: CarouselSizeContent & {
    /** @description URL pattern to display the carousel. Use * as wildcard. Example: /outlet* */
    matcher?: string;
    highlights: NonNullable<CarouselSizeContent["highlights"]>;
  };
  /** @description CrossSelling shelf configuration for 404 pages */
  crossSellingShelfProps?: CrossSellingShelfProps;
  /** @description 0 for ?page=0 as your first page */
  startingPage?: 0 | 1;
  /** @hidden */
  partial?: "hideMore" | "hideLess";
}
export const loader = (props: Props, req: Request) => {
  const {
    customTitles,
    carouselSize,
    page,
    cardLayout,
    layout,
    collections,
    crossSellingShelfProps,
    startingPage = 0,
    partial,
  } = props;
  const { pathname, search } = new URL(req.url);
  const customTitle = customTitles?.find(({ matcher }) =>
    matchPathname(matcher, pathname)
  );
  const { matcher, ...carouselSizeContent } = carouselSize ??
    ({} as Partial<NonNullable<typeof carouselSize>>);
  const resolvedCarouselSize = carouselSize &&
      (!matcher || matchPathname(matcher, pathname))
    ? {
      ...carouselSizeContent,
      highlights: withDynamicOutletHighlights(
        carouselSizeContent.highlights,
        pathname,
      ),
    }
    : undefined;

  if (!page) {
    return {
      ...props,
      url: req.url,
      customTitle,
      carouselSize: resolvedCarouselSize,
      startingPage,
      partial,
    };
  }

  return {
    customTitle,
    carouselSize: resolvedCarouselSize,
    page: {
      ...page,
      products: page?.products,
      filters: mergeCarouselSizeFilter(
        page.filters,
        resolvedCarouselSize?.highlights,
        pathname,
        search,
      ),
      pageInfo: {
        ...page?.pageInfo,
      },
    },
    cardLayout,
    collections,
    layout,
    crossSellingShelfProps,
    url: req.url,
    startingPage,
    partial,
  };
};
export default SearchTitleAndResult;
